import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { clampPanelWidth, PANEL_MAX_SHARE, PANEL_MIN, PANEL_STEP } from './code-panel.js';

/**
 * The conversation bar's panel button (docs/design/mockups/KodPanel.dc.html,
 * KodPanelKapali.dc.html, D-99): the sidebar icon mirrored, pressed while the
 * panel is shown. While the panel is hidden, a green dot says the workspace is
 * running or a preview is ready.
 */
export function PanelToggle({ hidden, running, onToggle }: { hidden: boolean; running: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const dot = hidden && running;
  return (
    <button
      type="button"
      className={hidden ? 'panel-toggle' : 'panel-toggle panel-toggle--on'}
      aria-pressed={!hidden}
      aria-label={hidden ? (dot ? t('codePanel.showRunningLabel') : t('codePanel.show')) : t('codePanel.hide')}
      title={hidden ? (dot ? t('codePanel.showRunning') : t('codePanel.show')) : t('codePanel.hide')}
      onClick={onToggle}
      data-testid="panel-toggle"
    >
      <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        {hidden ? null : <rect x="15" y="4" width="6" height="16" fill="currentColor" stroke="none" opacity="0.18" />}
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M15 4v16" />
      </svg>
      {dot ? <span className="panel-toggle__dot" aria-hidden="true" data-testid="panel-toggle-dot" /> : null}
    </button>
  );
}

/**
 * The line between the conversation and the panel: dragged or moved with the
 * arrow keys, it sets the panel's width (340 px to 60% of the space). The
 * width shows at once (a CSS variable on the layout); it is kept when the
 * drag ends.
 */
export function PanelDivider({ layout, width, onChange }: { layout: RefObject<HTMLDivElement | null>; width: number | null; onChange: (width: number) => void }) {
  const { t } = useTranslation();
  const [size, setSize] = useState<{ total: number; now: number }>({ total: 0, now: width ?? 0 });
  const dragging = useRef<number | null>(null);
  // where on the line the pointer took it: the panel's edge stays under the pointer while it moves
  const grab = useRef(0);

  // what the panel measures now and the space it shares (the separator's value and its range)
  const sideWidth = () => Math.round(layout.current?.querySelector<HTMLElement>(':scope > .conversation-layout__side')?.getBoundingClientRect().width ?? 0);
  useEffect(() => {
    const element = layout.current;
    if (!element) return;
    const measure = () => setSize({ total: element.clientWidth, now: Math.round(element.querySelector<HTMLElement>(':scope > .conversation-layout__side')?.getBoundingClientRect().width ?? 0) });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    // shown again after being hidden, the panel has its size back
    const side = element.querySelector<HTMLElement>(':scope > .conversation-layout__side');
    if (side) observer.observe(side);
    return () => observer.disconnect();
  }, [layout, width]);

  const apply = (next: number) => {
    layout.current?.style.setProperty('--panel-width', `${next}px`);
    setSize((current) => ({ ...current, now: next }));
  };
  const widthAt = (clientX: number) => {
    const rect = layout.current?.getBoundingClientRect();
    return rect ? clampPanelWidth(rect.right - clientX - grab.current, rect.width) : PANEL_MIN;
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const rect = layout.current?.getBoundingClientRect();
    const now = sideWidth();
    grab.current = rect ? rect.right - event.clientX - now : 0;
    dragging.current = now || widthAt(event.clientX);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (dragging.current === null) return;
    dragging.current = widthAt(event.clientX);
    apply(dragging.current);
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (dragging.current === null) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    const final = dragging.current;
    dragging.current = null;
    onChange(final);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? PANEL_STEP * 4 : PANEL_STEP;
    const total = layout.current?.clientWidth ?? size.total;
    const now = sideWidth() || width || PANEL_MIN;
    // the line moves the way the arrow points: left makes the panel wider
    const next = event.key === 'ArrowLeft' ? now + step : event.key === 'ArrowRight' ? now - step : event.key === 'Home' ? PANEL_MIN : event.key === 'End' ? total : null;
    if (next === null) return;
    event.preventDefault();
    const clamped = clampPanelWidth(next, total);
    apply(clamped);
    onChange(clamped);
  };
  const max = Math.max(PANEL_MIN, Math.round(size.total * PANEL_MAX_SHARE));
  return (
    <div
      className="panel-divider"
      role="separator"
      aria-orientation="vertical"
      aria-label={t('codePanel.width')}
      aria-valuemin={PANEL_MIN}
      aria-valuemax={max}
      aria-valuenow={Math.min(max, Math.max(PANEL_MIN, size.now))}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      data-testid="panel-divider"
    >
      <span className="panel-divider__grip" aria-hidden="true" />
    </div>
  );
}
